import React, { ReactNode } from 'react';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import { C, hexToRgba, lcarsLabelSx } from '../theme/tokens';

/** The one header every panel in the app shares: a small square accent
 * mark + an uppercase label, on a bottom hairline — extracted so a vault
 * row, the fitting form, and the connection diagram all draw the identical
 * header instead of five hand-tweaked approximations. */
const PanelHeader: React.FC<{ label: string; accent: string; action?: ReactNode; dense?: boolean; sx?: SxProps<Theme> }> = ({
  label,
  accent,
  action,
  dense,
  sx,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      // A header is a flex ROW, where min-width:auto applies on the main
      // axis — so a wide `action` (the diagram's four-badge mass legend) made
      // the header, and therefore its whole panel, wider than the viewport
      // instead of wrapping. Caught at 390px by preview/shoot.mjs.
      flexWrap: 'wrap',
      rowGap: 0.75,
      minWidth: 0,
      px: dense ? 0 : 2.5,
      py: dense ? 0 : 1.5,
      mb: dense ? 1.25 : 0,
      borderBottom: dense ? 'none' : `1px solid ${C.hairline}`,
      ...sx,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      {/* A short accent bar, rounded like everything else in this world —
          the LCARS elbow tab lives on `elbowTabSx` for the one or two panels
          that want it, not on every header in the app. */}
      <Box sx={{ width: 3, height: 14, borderRadius: 999, backgroundColor: accent, boxShadow: `0 0 8px ${hexToRgba(accent, 0.6)}`, flexShrink: 0 }} />
      <Typography sx={lcarsLabelSx(accent, '0.8rem')}>{label}</Typography>
    </Box>
    {action}
  </Box>
);

export default PanelHeader;
